import { ref, readonly, computed, onMounted } from 'vue'
import type { PublicReleaseInfo } from '@/utils/app-release-compare'
import { isSemverGreater } from '@/utils/app-release-compare'
import { runningAppRelease as runningRelease } from '@/utils/running-app-release'

const APP_CACHE_PREFIX = 'tb-ticket-admin-app-shell-'
const CHECK_INTERVAL_MS = 30 * 1000
const FOREGROUND_RECHECK_THROTTLE_MS = 5 * 1000
const VERSION_ENDPOINT = '/meta/version.json'
const RESET_TIMEOUT_MS = 4000
const RELOAD_DELAY_MS = 150

const hasUpdateFlag = ref(false)
const latestRelease = ref<PublicReleaseInfo | null>(null)
const dismissedRelease = ref<PublicReleaseInfo | null>(null)
const checking = ref(false)
const isUpdating = ref(false)
const updatePhase = ref<'idle' | 'preparing' | 'resetting' | 'unregistering' | 'reloading'>('idle')
const updateProgress = ref(0)

let pollingStarted = false
let lastCheckAt = 0

export async function fetchPublicRelease(): Promise<PublicReleaseInfo | null> {
  try {
    const url = new URL(VERSION_ENDPOINT, window.location.origin)
    url.searchParams.set('_tb_update_check', String(Date.now()))
    const res = await fetch(url.toString(), {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    })
    if (!res.ok) return null
    const data = (await res.json()) as { version?: string; buildId?: string }
    const version = typeof data.version === 'string' ? data.version.trim() : ''
    if (!version) return null
    const buildId = typeof data.buildId === 'string' ? data.buildId.trim() : undefined
    return { version, ...(buildId ? { buildId } : {}) }
  } catch {
    return null
  }
}

async function checkForUpdateInternal(): Promise<void> {
  if (checking.value || isUpdating.value) return
  checking.value = true
  lastCheckAt = Date.now()

  try {
    const remote = await fetchPublicRelease()
    if (!remote) return

    latestRelease.value = remote
    const buildChanged = !import.meta.env.DEV
      && remote.version === runningRelease.version
      && Boolean(remote.buildId && remote.buildId !== runningRelease.buildId)
    const dismissed = dismissedRelease.value
    const isDismissed = dismissed?.version === remote.version && dismissed?.buildId === remote.buildId
    hasUpdateFlag.value = !isDismissed && (isSemverGreater(remote.version, runningRelease.version) || buildChanged)
  } finally {
    checking.value = false
  }
}

function setUpdateStep(
  phase: 'idle' | 'preparing' | 'resetting' | 'unregistering' | 'reloading',
  progress: number,
): void {
  updatePhase.value = phase
  updateProgress.value = progress
}

async function resetServiceWorkerAndCaches(): Promise<void> {
  setUpdateStep('resetting', 35)

  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'TB_FORCE_RESET' })
  }

  if ('caches' in window) {
    const names = await caches.keys()
    await Promise.all(names.filter((name) => name.startsWith(APP_CACHE_PREFIX)).map((name) => caches.delete(name)))
  }

  setUpdateStep('unregistering', 70)

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((r) => r.unregister()))
  }

  setUpdateStep('reloading', 92)
}

export async function reloadApp(): Promise<void> {
  if (isUpdating.value) return
  isUpdating.value = true
  setUpdateStep('preparing', 12)

  let timeout: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      resetServiceWorkerAndCaches(),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new Error('app_cache_reset_timeout')), RESET_TIMEOUT_MS)
      }),
    ])
  } catch {
    console.warn('[AppUpdate] Cache reset did not complete; attempting a fresh page load.')
  } finally {
    clearTimeout(timeout)
  }

  setUpdateStep('reloading', 92)
  setTimeout(() => {
    setUpdateStep('reloading', 100)
    const nextUrl = new URL(window.location.href)
    nextUrl.searchParams.set('_tb_update', String(Date.now()))
    window.location.replace(nextUrl.toString())
  }, RELOAD_DELAY_MS)
}

function dismissUpdate(): void {
  const rel = latestRelease.value
  if (rel) {
    dismissedRelease.value = rel
    hasUpdateFlag.value = false
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState !== 'visible') return
  if (Date.now() - lastCheckAt < FOREGROUND_RECHECK_THROTTLE_MS) return
  void checkForUpdateInternal()
}

let visibilityHooked = false
let foregroundHooked = false

function startUpdatePolling(): void {
  if (pollingStarted) return
  pollingStarted = true
  void checkForUpdateInternal()
  window.setInterval(() => {
    void checkForUpdateInternal()
  }, CHECK_INTERVAL_MS)
}

function checkSoonAfterForeground(): void {
  if (Date.now() - lastCheckAt < FOREGROUND_RECHECK_THROTTLE_MS) return
  void checkForUpdateInternal()
}

export function useAppUpdate() {
  const latestVersion = computed(() => latestRelease.value?.version ?? null)
  const currentVersion = computed(() => runningRelease.version)

  onMounted(() => {
    startUpdatePolling()
    if (!visibilityHooked) {
      visibilityHooked = true
      document.addEventListener('visibilitychange', onVisibilityChange)
    }
    if (!foregroundHooked) {
      foregroundHooked = true
      window.addEventListener('focus', checkSoonAfterForeground)
      window.addEventListener('online', checkSoonAfterForeground)
      window.addEventListener('pageshow', checkSoonAfterForeground)
    }
  })

  return {
    hasUpdate: readonly(hasUpdateFlag),
    latestRelease: readonly(latestRelease),
    currentVersion,
    latestVersion,
    checking: readonly(checking),
    isUpdating: readonly(isUpdating),
    updatePhase: readonly(updatePhase),
    updateProgress: readonly(updateProgress),
    checkForUpdate: checkForUpdateInternal,
    reloadApp,
    dismissUpdate,
  }
}
